import { onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useGlossaryStore } from '../store';
import {
  extractGlossaryConfig,
  isGlossaryLink,
  parseGlossaryHash,
  GLOSSARY_LINK_CLASSES,
  type GlossaryLinkConfig,
} from '../utils/glossaryLinks';

/**
 * Composable for handling special glossary links
 *
 * This composable sets up event listeners and handlers for special glossary links
 * that the marketing team can use in their content.
 */
export function useGlossaryLinks() {
  const route = useRoute();
  const router = useRouter();
  const glossaryStore = useGlossaryStore();

  /**
   * Handle click events on glossary links
   */
  const handleGlossaryLinkClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const linkElement = target.closest('a');

    if (!linkElement || !isGlossaryLink(linkElement)) {
      return;
    }

    event.preventDefault();

    const config = extractGlossaryConfig(linkElement);
    if (!config) {
      return;
    }

    // Add loading state
    linkElement.classList.add(GLOSSARY_LINK_CLASSES.LOADING);

    openGlossaryFromConfig(config).finally(() => {
      linkElement.classList.remove(GLOSSARY_LINK_CLASSES.LOADING);
    });
  };

  /**
   * Handle hash changes in the URL
   */
  const handleHashChange = () => {
    const hash = window.location.hash;
    const config = parseGlossaryHash(hash);

    if (config) {
      openGlossaryFromConfig(config);
    }
  };

  /**
   * Open glossary based on configuration
   */
  const openGlossaryFromConfig = async (config: GlossaryLinkConfig) => {
    try {
      if (config.term) {
        // Open to specific term
        await glossaryStore.openGlossary(config.term);
      } else if (config.search) {
        // Open with search
        glossaryStore.openGlossary();
        await glossaryStore.searchGlossary(config.search);
      } else if (config.letter) {
        // Open to specific letter
        glossaryStore.openGlossary();
        await glossaryStore.filterByLetter(config.letter);
      } else {
        // Just open glossary
        glossaryStore.openGlossary();
      }
    } catch (error) {
      console.error('Error opening glossary:', error);
      // Still open glossary even if specific action fails
      glossaryStore.openGlossary();
    }
  };

  /**
   * Initialize glossary from URL hash on page load
   */
  const initializeFromHash = () => {
    const hash = window.location.hash;
    const config = parseGlossaryHash(hash);

    if (config) {
      // Small delay to ensure components are mounted
      setTimeout(() => {
        openGlossaryFromConfig(config);
      }, 100);
    }
  };

  /**
   * Set up event listeners for glossary links in dynamic content
   */
  const setupGlossaryLinks = () => {
    // Use event delegation to handle dynamically added links
    document.addEventListener('click', handleGlossaryLinkClick);
    window.addEventListener('hashchange', handleHashChange);

    // Initialize from current hash
    initializeFromHash();
  };

  /**
   * Clean up event listeners
   */
  const cleanupGlossaryLinks = () => {
    document.removeEventListener('click', handleGlossaryLinkClick);
    window.removeEventListener('hashchange', handleHashChange);
  };

  /**
   * Manually trigger glossary opening from config
   * Useful for programmatic glossary opening
   */
  const openGlossary = (config: GlossaryLinkConfig) => {
    return openGlossaryFromConfig(config);
  };

  /**
   * Update all glossary links on the page with active state
   */
  const updateGlossaryLinksState = () => {
    const glossaryLinks = document.querySelectorAll(
      `.${GLOSSARY_LINK_CLASSES.LINK}`,
    );

    glossaryLinks.forEach((link) => {
      const config = extractGlossaryConfig(link as HTMLElement);

      if (config) {
        const isActive =
          (config.term && glossaryStore.currentTerm === config.term) ||
          (config.search && glossaryStore.searchQuery === config.search) ||
          (config.letter && glossaryStore.selectedLetter === config.letter);

        if (isActive) {
          link.classList.add(GLOSSARY_LINK_CLASSES.ACTIVE);
        } else {
          link.classList.remove(GLOSSARY_LINK_CLASSES.ACTIVE);
        }
      }
    });
  };

  /**
   * Scan for and enhance existing glossary links
   * This is useful for content that's already on the page
   */
  const enhanceExistingLinks = () => {
    const potentialLinks = document.querySelectorAll(
      'a[data-glossary-term], a[data-glossary-search], a[data-glossary-letter]',
    );

    potentialLinks.forEach((link) => {
      link.classList.add(GLOSSARY_LINK_CLASSES.LINK);
    });
  };

  /**
   * Setup lifecycle hooks
   */
  onMounted(() => {
    setupGlossaryLinks();
    enhanceExistingLinks();

    // Watch for store changes to update link states
    // This could be enhanced with a watcher if needed
  });

  onUnmounted(() => {
    cleanupGlossaryLinks();
  });

  return {
    openGlossary,
    updateGlossaryLinksState,
    enhanceExistingLinks,
    setupGlossaryLinks,
    cleanupGlossaryLinks,
  };
}

/**
 * Utility function to register glossary links in a specific container
 * Useful for components that render dynamic content
 */
export function registerGlossaryLinksInContainer(container: HTMLElement) {
  const glossaryLinks = container.querySelectorAll(
    'a[data-glossary-term], a[data-glossary-search], a[data-glossary-letter]',
  );

  glossaryLinks.forEach((link) => {
    link.classList.add(GLOSSARY_LINK_CLASSES.LINK);
  });
}

/**
 * Vue directive for automatically registering glossary links
 * Usage: v-glossary-links
 */
export const vGlossaryLinks = {
  mounted(el: HTMLElement) {
    registerGlossaryLinksInContainer(el);
  },
  updated(el: HTMLElement) {
    registerGlossaryLinksInContainer(el);
  },
};
